// @ts-ignore
import { io, Socket } from 'socket.io-client';

import RealtimeSubject from '../enum/RealtimeSubject';
import { RealtimeOperationPayload } from '../type/RealtimeOperationPayload';

type OperationHandler = (payload: RealtimeOperationPayload) => void;
type ReconnectHandler = (attempts: number) => void;

export interface RealtimeClientOptions {
    host: string
    path: string
    logger: any
}

export class RealtimeClient {

    private options: RealtimeClientOptions
    private logger: any
    private socket: Socket

    private reconnectHandler: ReconnectHandler;

    // @ts-ignore
    private handlers = new Map<RealtimeSubject, OperationHandler>()

    private queue = [];

    // Clock-offset state. `serverNow()` returns Date.now() + serverTimeOffset
    // so screens in a synchronized System can compute their slide index
    // against a shared reference. Calibrated once on every connect (~5
    // round-trips) and then periodically only while a sync interval is
    // active — see startPeriodicClockSync().
    private serverTimeOffset: number = 0;
    private clockSyncInterval: any = null;
    private visibilityChangeHandler: (() => void) | null = null;

    constructor(options: RealtimeClientOptions) {

        this.logger = options.logger?.child('Realtime');
        this.options = options;
    }

    connect(token: string){

        if(this.socket){
            this.disconnect();
        }

        const socket = io(this.options.host, {
            path: this.options.path,
            auth: {
                token
            }
        });

        socket.io.on('reconnect', (attempts) =>
        {
            this.logger.info(`Socket reconnected after ${attempts} attempts`);

            // Wall clocks may have drifted while disconnected — recalibrate
            // before notifying the app, which may already trust serverNow().
            this.syncClock().catch(err => this.logger.info('Clock resync after reconnect failed', err));

            if(this.reconnectHandler){
                this.reconnectHandler(attempts);
            }
        });

        socket.on('connect', () => {
            this.logger.info('Socket connected');

            // One offset measurement on every connect, regardless of whether
            // this System currently uses sync — cheap (a few hundred bytes)
            // and lets a sync-on toggle work immediately without waiting for
            // the first interval tick.
            this.syncClock().catch(err => this.logger.info('Initial clock sync failed', err));
        });

        socket.on('connect_error', (error) => {
            this.logger.info('Socket connection failed', error);
        });

        socket.on('disconnect', () => {
            this.logger.info('Socket disconnected');
        });

        socket.on('operation', data => {

            this.logger.info('Received operation', data);

            const { subject } = data;
            if(!subject){
                this.logger.error('Invalid payload received');
                return;
            }

            const handler = this.handlers.get(subject);
            if(!handler){
                this.logger.error('No handler for subject', subject);
                return;
            }

            this.enqueue(() => handler(data));
        });

        this.socket = socket;
    }

    disconnect(){

        this.stopPeriodicClockSync();

        if(!this.socket){
            return;
        }

        this.socket.disconnect();
        this.socket = null;
    }

    /**
     * Server-time clock (Date.now() + measured offset). Falls back to local
     * Date.now() before the first sample lands.
     */
    serverNow(): number {

        return Date.now() + this.serverTimeOffset;
    }

    /**
     * Cristian's algorithm: send N pings, keep the offset from the sample
     * with the lowest round-trip time (minimizes asymmetric-latency error).
     * Each sample: t0 → emit time:sync → ack carries serverT → t1 arrives.
     * Estimated server time at midpoint = serverT; estimated client time at
     * midpoint = (t0 + t1) / 2; so offset = serverT - (t0 + t1) / 2.
     */
    async syncClock(samples: number = 5, spacingMs: number = 200): Promise<void> {

        if(!this.socket){
            return;
        }

        const socket = this.socket;
        let best: { offset: number, rtt: number } | null = null;

        for(let i=0; i<samples; i++){

            const result = await new Promise<{ offset: number, rtt: number } | null>(resolve => {

                const t0 = Date.now();
                const timeout = setTimeout(() => resolve(null), 5000);

                socket.emit('time:sync', t0, (resp: { serverT: number } | undefined) => {

                    clearTimeout(timeout);

                    if(!resp || typeof resp.serverT !== 'number'){
                        resolve(null);
                        return;
                    }

                    const t1 = Date.now();
                    const rtt = t1 - t0;
                    const offset = resp.serverT - (t0 + t1) / 2;

                    resolve({ offset, rtt });
                });
            });

            if(result && (!best || result.rtt < best.rtt)){
                best = result;
            }

            // Pause between samples so we measure different network moments
            // instead of a coalesced burst. Skip the trailing sleep.
            if(i < samples - 1){
                await new Promise(r => setTimeout(r, spacingMs));
            }
        }

        if(best){
            this.serverTimeOffset = best.offset;
            this.logger.info(`Clock synced — offset=${Math.round(best.offset)}ms, rtt=${best.rtt}ms`);
        }
        else {
            this.logger.info('Clock sync produced no usable samples');
        }
    }

    /**
     * Start (or restart) the periodic recalibration loop. Only call this
     * when the connected screen actually uses sync — wall-clock drift on
     * non-sync screens doesn't matter. visibilitychange triggers an immediate
     * resync to catch wake-from-sleep clock jumps.
     */
    startPeriodicClockSync(intervalMs: number = 5 * 60 * 1000): void {

        this.stopPeriodicClockSync();

        this.clockSyncInterval = setInterval(() => {
            this.syncClock().catch(err => this.logger.info('Periodic clock sync failed', err));
        }, intervalMs);

        if(typeof document !== 'undefined' && typeof document.addEventListener === 'function'){
            this.visibilityChangeHandler = () => {
                if(document.visibilityState === 'visible'){
                    this.syncClock().catch(err => this.logger.info('Visibility clock sync failed', err));
                }
            };
            document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        }
    }

    stopPeriodicClockSync(): void {

        if(this.clockSyncInterval){
            clearInterval(this.clockSyncInterval);
            this.clockSyncInterval = null;
        }
        if(this.visibilityChangeHandler && typeof document !== 'undefined'){
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }
    }

    handler(subject: RealtimeSubject, handler: OperationHandler): this {

        this.handlers.set(subject, handler);
        return this;
    }

    removeHandler(subject){

        this.handlers.delete(subject);
    }

    onReconnect(handler: ReconnectHandler): this {

        this.reconnectHandler = handler;
        return this;
    }

    dequeue(){

        if (!this.queue.length) return

        const handlerResult = this.queue[0]();

        const done = () => {
            this.queue.shift();
            this.dequeue();
        }

        if(handlerResult){
            handlerResult.finally(done);
        }
        else {
            done();
        }
    }

    enqueue(func){

        this.queue.push(func);

        if (this.queue.length === 1){
            this.dequeue()
        }
    }
}